import ProductList from "./ProductList";
import { ProductGridSkeleton } from "./ProductCardSkeleton";
import { useCatalog } from "../contexts/CatalogContext";
import { useSiteConfig } from "../contexts/SiteConfigContext";
import { useEffect, useMemo } from "react";
import { findOrGetImages } from "../api";
import { byCategoryThenMerchandisingOrder } from "../extensions/helpers";

/**
 * Homepage product showcase, split into two carousels: **Classics** (a curated,
 * admin-managed set) and **Designs** (everything else). Side by side on
 * desktop, stacked with Classics on top below `lg`.
 *
 * The grouping is a list of Square ITEM ids in the site-config Blobs store
 * (admin portal → Product Management → Homepage Groups), deliberately NOT a
 * Square category: a category is what ProductCard / sitemap.js /
 * product-meta.js derive a product's canonical URL from, so filing products
 * into a "Classics" category would silently rewrite their URLs and orphan the
 * indexed ones.
 *
 * Designs is the remainder rather than its own list, so a newly added product
 * always shows up somewhere on the homepage instead of silently vanishing.
 * The opt-out is explicit: `homepageExcludedItemIds` ("Neither" in the admin
 * panel) drops a product from both carousels. Those products are still fully
 * on sale — the shop, search, sitemap and their own pages are untouched; only
 * the homepage showcase skips them.
 *
 * If either group ends up empty (nothing configured, or the config fetch
 * failed), this falls back to a single ungrouped carousel — which still
 * honours the exclusions.
 */
const FeaturedProductList = () => {
  const { items, categories, addImages, getImagesByIds } = useCatalog();
  const { classicItemIds, homepageExcludedItemIds } = useSiteConfig();

  // The catalog provider loads the full item list up front; the homepage
  // carousels showcase whatever is in the catalog (no hardcoded category),
  // so we just make sure their images are loaded.
  useEffect(() => {
    const retrieveImages = async () => {
      try {
        await findOrGetImages(items, addImages, getImagesByIds);
      } catch (error) {
        console.error("Get Catalog failed:", error);
      }
    };

    if (items.length > 0) {
      retrieveImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const { shown, classics, designs } = useMemo(() => {
    const classicIds = new Set(classicItemIds);
    const excludedIds = new Set(homepageExcludedItemIds);
    // "Neither" is applied first, so an id in both lists is excluded rather
    // than shown under Classics.
    const shownItems = excludedIds.size
      ? items.filter((i) => !i.id || !excludedIds.has(i.id))
      : items;
    // A configured id that's no longer in the catalog (product deleted or
    // hidden) just drops out — no cleanup needed in the config.
    // Both carousels read in the owner's own arrangement (Square "Rearrange
    // Items"). ProductList used to impose that order itself; it no longer
    // sorts, so the ordering has to be applied here.
    //
    // These carousels MIX categories, so they order by CATEGORY first (hats
    // before t-shirts, per the dashboard) and then by each item's position
    // within its own category. Item ordinals are only comparable within a
    // single category — sorting a mixed list by them alone interleaves
    // categories arbitrarily.
    return {
      shown: shownItems,
      classics: byCategoryThenMerchandisingOrder(
        shownItems.filter((i) => i.id && classicIds.has(i.id)),
        categories,
      ),
      designs: byCategoryThenMerchandisingOrder(
        shownItems.filter((i) => !i.id || !classicIds.has(i.id)),
        categories,
      ),
    };
  }, [items, categories, classicItemIds, homepageExcludedItemIds]);

  if (items.length === 0) {
    return (
      <div className="w-full flex flex-col gap-6">
        {/* Carousel variant — the products land in ProductCarousel's
            fixed-width scroll row, not the page grid. */}
        <ProductGridSkeleton count={4} variant="carousel" />
      </div>
    );
  }

  // Every product excluded — render nothing rather than an empty carousel
  // frame. (The shop is unaffected; this is only the homepage showcase.)
  if (shown.length === 0) return null;

  // No grouping configured (or a config blip): one carousel, as before —
  // but of the SHOWN items, so "Neither" still means neither.
  if (classics.length === 0 || designs.length === 0) {
    return (
      <div className="w-full flex flex-col gap-6">
        <ProductList
          items={byCategoryThenMerchandisingOrder(shown, categories)}
          isCarousel
        />
      </div>
    );
  }

  return (
    // Stacked, the two groups need only enough space to read as separate
    // sections — their headings already do most of that work, and a large gap
    // pushed the second carousel off a phone screen entirely.
    //
    // Side by side it's a flex row, not a 50/50 grid: Classics takes its
    // content width (a handful of products shouldn't reserve half the screen
    // and an arrow that scrolls nowhere) and Designs starts right after it,
    // with `max-w-1/2` restoring the even split as soon as Classics has enough
    // products to overflow. The arrows come back on their own — ProductCarousel
    // hides them whenever its cards fit.
    <div className="w-full flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
      <section
        className="flex flex-col gap-1 lg:gap-3 min-w-0 lg:flex-initial lg:max-w-[50%]"
        aria-labelledby="home-group-classics"
      >
        <h2
          id="home-group-classics"
          className="text-2xl md:text-3xl text-center lora-text text-brown-dark"
        >
          Classics
        </h2>
        <ProductList items={classics} isCarousel />
      </section>
      {/* The divider only makes sense while the columns sit side by side; once
          they stack, the gap already separates them. */}
      <section
        className="flex flex-col gap-1 lg:gap-3 min-w-0 lg:flex-1 lg:border-l lg:border-accent-light lg:pl-6"
        aria-labelledby="home-group-designs"
      >
        <h2
          id="home-group-designs"
          className="text-2xl md:text-3xl text-center lora-text text-brown-dark"
        >
          Designs
        </h2>
        <ProductList items={designs} isCarousel />
      </section>
    </div>
  );
};

export default FeaturedProductList;
